import type { CalendarDay } from './bookedNight';
import { addDays, getOccupiedNightKeys, nightsBetween } from './calendarDates';

/** A booking of ours, as the calendar needs it (nights from checkIn up to, not including, checkOut; both as YYYY-MM-DD). */
export interface StayBooking {
  checkIn: string;
  checkOut: string;
  /** The guest's name; empty when it is not known. */
  guestName?: string;
  /** Adults and children staying (infants not counted). */
  guests?: number;
}

/** A run of booked nights drawn as one bar on the calendar. */
export interface Stay {
  /** First and last night. */
  from: string;
  to: string;
  /** 'unpaqd': booked on Unpaqd, 'other': booked on another platform (only "Booked" is shown). */
  kind: 'unpaqd' | 'other';
  label: string;
  initial: string;
}

/** What one day's tile draws of its stay. */
export interface TileStay {
  kind: Stay['kind'];
  label: string;
  initial: string;
  /** The stay's first night: the bar starts here, rounded. */
  startsHere: boolean;
  /** The stay's last night: the bar ends here, rounded. */
  endsHere: boolean;
  /** The label goes on this tile (where the stay starts, or a new week row or month begins). */
  showLabel: boolean;
  /** How many tiles of this week row the label can run over (this one and the following nights of the stay). */
  spanInRow: number;
}

/** "Sanket +4": the guest's first name and how many more guests come with them. */
export const stayLabel = (guestName: string | undefined, guests: number | undefined): string => {
  const first = (guestName || '').trim().split(/\s+/)[0] || 'Guest';
  return guests && guests > 1 ? `${first} +${guests - 1}` : first;
};

/**
 * The bars to draw: one per booking of ours (with the guest and how many come), and one per run of nights booked
 * elsewhere (Airbnb, another platform), which only says "Booked". A night Unpaqd booked but whose details did not load is a
 * plain "Booked" bar too.
 */
export const buildStays = (
  days: Record<string, CalendarDay | undefined>,
  bookings: StayBooking[],
  /** YYYY-MM-DD: nights before it are never drawn, so they are not scanned (a long calendar history stays cheap). */
  today?: string,
): Stay[] => {
  const stays: Stay[] = [];
  const covered = new Set<string>();

  bookings.forEach((b) => {
    if (!b.checkIn) return;
    // Only nights the calendar marks BOOKED get the bar (a night it shows as available or released can still be sold, and a
    // click on it must not open a booking). A night the calendar has no entry for is taken as the booking says.
    const nightKeys = getOccupiedNightKeys(b.checkIn, b.checkOut).filter((k) => days[k] === undefined || days[k]?.status === 'BOOKED');
    if (nightKeys.length === 0) return;
    nightKeys.forEach((k) => covered.add(k));
    const label = stayLabel(b.guestName, b.guests);
    // one bar per run of consecutive nights (the booking is split where the calendar does not hold it)
    let from = nightKeys[0];
    nightKeys.forEach((k, i) => {
      const next = nightKeys[i + 1];
      if (next !== addDays(k, 1)) {
        stays.push({ from, to: k, kind: 'unpaqd', label, initial: label.charAt(0).toUpperCase() });
        from = next;
      }
    });
  });

  // nights the calendar marks booked that no booking of ours explains, grouped into runs of the same kind
  const uncovered = Object.keys(days)
    .filter((d) => (!today || d >= today) && days[d]?.status === 'BOOKED' && !covered.has(d))
    .sort();
  let run: { from: string; to: string; kind: Stay['kind'] } | null = null;
  const flush = (): void => {
    if (run) {
      stays.push({
        from: run.from,
        to: run.to,
        kind: run.kind,
        label: 'Booked',
        initial: run.kind === 'unpaqd' ? 'U' : 'B',
      });
    }
    run = null;
  };
  uncovered.forEach((d) => {
    const kind: Stay['kind'] = days[d]?.bysBooking === true ? 'unpaqd' : 'other';
    if (run && run.kind === kind && addDays(run.to, 1) === d) {
      run.to = d;
    } else {
      flush();
      run = { from: d, to: d, kind };
    }
  });
  flush();
  return stays;
};

/** The stays by night, built once per set of stays so a tile does not scan every stay. */
export type StayIndex = Record<string, Stay[]>;

export const indexStays = (stays: Stay[], today?: string): StayIndex => {
  const index: StayIndex = {};
  stays.forEach((s) => {
    // nights before today are never drawn
    for (let d = today && s.from < today ? today : s.from; d <= s.to; d = addDays(d, 1)) {
      (index[d] = index[d] || []).push(s);
    }
  });
  return index;
};

/**
 * What a day's tile draws of the stay it belongs to, or null when no stay covers the day. `today` (YYYY-MM-DD) is the first day
 * the calendar shows: a stay that began before it starts there (its bar is rounded and labelled from today). Two stays on the
 * same night (a double booking) show as "2 bookings", so the host sees the clash.
 */
export const stayAt = (dateStr: string, stays: Stay[] | StayIndex, today?: string): TileStay | null => {
  const matches = Array.isArray(stays) ? stays.filter((s) => dateStr >= s.from && dateStr <= s.to) : stays[dateStr] || [];
  if (matches.length === 0) return null;
  if (matches.length > 1) {
    return {
      kind: 'unpaqd',
      label: `${matches.length} bookings`,
      initial: '!',
      startsHere: false,
      endsHere: false,
      showLabel: true,
      spanInRow: 1,
    };
  }
  const stay = matches[0];
  const firstVisible = today && stay.from < today ? today : stay.from;
  const startsHere = dateStr === firstVisible;
  const endsHere = dateStr === stay.to;
  const dayOfWeek = new Date(`${dateStr}T00:00:00`).getDay(); // weeks start on Sunday
  const dayOfMonth = Number(dateStr.slice(8, 10));
  const nightsLeft = nightsBetween(dateStr, stay.to) + 1;
  // the calendar does not show the neighbouring months, so a label cannot run past the end of its month
  const d = new Date(`${dateStr}T00:00:00`);
  const daysLeftInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() - d.getDate() + 1;
  return {
    kind: stay.kind,
    label: stay.label,
    initial: stay.initial,
    startsHere,
    endsHere,
    showLabel: startsHere || dayOfWeek === 0 || dayOfMonth === 1,
    spanInRow: Math.max(1, Math.min(nightsLeft, 7 - dayOfWeek, daysLeftInMonth)),
  };
};
